import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  // Pages with dark hero backgrounds where transparent header looks good
  const darkHeroPages = ['/', '/pcp-car-finance-claims', '/gap-insurance-claims', '/hp-claims'];
  const hasDarkHero = darkHeroPages.includes(location.pathname);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Force scrolled style on pages without dark hero
  const useScrolledStyle = isScrolled || !hasDarkHero;

  const navLinks = [
    { label: 'About Us', href: '/about' },
    { label: 'PCP Claims', href: '/pcp-car-finance-claims' },
    { label: 'GAP Insurance Claims', href: '/gap-insurance-claims' },
    { label: 'How It Works', href: '/how-it-works' },
    { label: 'Ask Our AI', href: '/faq#ask-ai' },
    { label: 'FAQ', href: '/faq' },
    { label: 'Contact', href: '/contact' },
    { label: 'MySolicitor.co.uk', href: '/mysolicitor' },
  ];

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        useScrolledStyle
          ? 'bg-background/95 backdrop-blur-md shadow-md py-3'
          : 'bg-transparent py-5'
      )}
    >
      <div className="section-container">
        <nav className="flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center">
            <img 
              src="/logo-bluelion-claims.png" 
              alt="Blue Lion Claims" 
              className="h-20 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className={cn(
                  'text-sm font-medium transition-colors duration-200',
                  useScrolledStyle
                    ? 'text-foreground/80 hover:text-primary'
                    : 'text-primary-foreground/90 hover:text-primary-foreground'
                )}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className={cn('w-6 h-6', useScrolledStyle ? 'text-foreground' : 'text-primary-foreground')} />
            ) : (
              <Menu className={cn('w-6 h-6', useScrolledStyle ? 'text-foreground' : 'text-primary-foreground')} />
            )}
          </button>
        </nav>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 bg-background shadow-xl border-t border-border animate-fade-in-down">
            <div className="flex flex-col p-6 gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-foreground/80 hover:text-primary font-medium py-2 transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export { Header };
